/**
 * Machine3D — the lazy boundary around the three.js viewer.
 *
 * Everything that imports `three` lives under `machine3d/` and is reached
 * only through the dynamic `import()` below, so Vite splits three.js and the
 * builders into their own chunk that downloads the first time a reader picks
 * the 3D view. This file itself is small and safe to import anywhere.
 *
 * The livery is read from the brand token `--brand-secondary` on `<html>` —
 * Komatsu yellow `#ffcc00`, Meridian pink `#c42c7a` — and re-read when the
 * brand changes. Surface drills have no 3D builder in the demo either; for
 * them (and when WebGL is unavailable) the `fallback` renders instead.
 *
 * UI layer.
 *
 * @see src/versions/v1/shared/viz/machine3d/Viewer3D.tsx
 * @see docs/logic/fleet/machine-page.md
 */

import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import type { ComponentHealth } from '../../data/core';
import type { Machine, MachineClass } from '../../schema/machine';
import { useBrand } from '../BrandContext';
import { useReducedMotion } from './useReducedMotion';

const Viewer3D = lazy(() => import('./machine3d/Viewer3D'));

/** Classes with a 3D builder. Demo line 1709 — `sdrill` has none. */
const CLASSES_3D: ReadonlySet<MachineClass> = new Set<MachineClass>([
  'lhd',
  'truck',
  'jumbo',
  'longhole',
]);

/** True when the class has a 3D model. */
export const hasMachine3D = (cls: MachineClass): boolean => CLASSES_3D.has(cls);

/** The demo's `PINK`, if the brand token cannot be read. */
const DEFAULT_PAINT = '#D62B80';

/** `--brand-secondary` on `<html>`, re-read on brand change. */
function useBrandPaint(): string {
  const { brandId } = useBrand();
  const [paint, setPaint] = useState(DEFAULT_PAINT);
  useEffect(() => {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-secondary')
      .trim();
    setPaint(/^#[0-9a-f]{6}$/i.test(v) ? v : DEFAULT_PAINT);
  }, [brandId]);
  return paint;
}

export interface Machine3DProps {
  machine: Pick<Machine, 'machine_id' | 'model' | 'cls'>;
  health: readonly ComponentHealth[];
  selected?: string | null;
  onSelectComponent?: (component: string) => void;
  /** Rendered for a class with no 3D model, and when WebGL is unavailable. */
  fallback: ReactNode;
}

export function Machine3D({
  machine,
  health,
  selected = null,
  onSelectComponent,
  fallback,
}: Machine3DProps) {
  const paint = useBrandPaint();
  const reduced = useReducedMotion();
  if (!hasMachine3D(machine.cls)) return <>{fallback}</>;
  return (
    <Suspense
      fallback={
        <div className="flex h-[340px] w-full items-center justify-center rounded-lg text-xs text-muted @4xl:h-[400px]">
          Loading the 3D model…
        </div>
      }
    >
      <Viewer3D
        cls={machine.cls}
        model={machine.model}
        machineId={machine.machine_id}
        health={health}
        selected={selected}
        onSelectComponent={onSelectComponent}
        paint={paint}
        reducedMotion={reduced}
        fallback={fallback}
      />
    </Suspense>
  );
}
